import { nativePlatform, pkce, type NativePlatform } from './native'

// Signing in from the app (native app spec, section 5): Keycloak opens in the system browser (ASWebAuthenticationSession on iOS, a
// Custom Tab on Android), the server hands back a one-time code, and the WebView swaps it for the session cookie.

/** The app's own sign-in plugin (mobile/): opens the URL in the system browser and resolves with the URL it came back on. */
export interface BossChatAuthPlugin {
  signIn(options: { url: string; callbackScheme: string }): Promise<{ url: string }>
}

export const CALLBACK_SCHEME = 'uk.co.pubinvest.chat'

// The plugin proxy answers every property, `then` included, so an async function must never resolve with it: awaiting it would
// call BossChatAuth.then(), which the app does not have. The sign-in function is handed out instead.
async function nativeSignInFn(): Promise<BossChatAuthPlugin['signIn']> {
  const { registerPlugin } = await import('@capacitor/core')
  const auth = registerPlugin<BossChatAuthPlugin>('BossChatAuth')
  return (options) => auth.signIn(options)
}

export type NativeSignInResult = 'signed-in' | 'cancelled' | 'failed'

/** Where the sign-in page is, for this platform and challenge. */
export function appLoginUrl(platform: NativePlatform, challenge: string, origin = location.origin): string {
  return `${origin}/auth/login?app=${platform}&challenge=${encodeURIComponent(challenge)}`
}

/** The code in the URL the browser came back on: uk.co.pubinvest.chat://auth?code=… or https://…/auth/app-callback?code=…. */
export function codeFrom(url: string): string | null {
  const query = url.includes('?') ? url.slice(url.indexOf('?') + 1) : ''
  return new URLSearchParams(query).get('code')
}

/** Opens Keycloak's sign-out in the system browser, which comes back to the app when done. Never throws. */
export async function endNativeBrowserSession(url: string, signIn?: BossChatAuthPlugin['signIn']): Promise<void> {
  try {
    const open = signIn ?? (await nativeSignInFn())
    await open({ url, callbackScheme: CALLBACK_SCHEME })
  } catch {
    // Closed by the person, or no browser: the chat session has ended either way.
  }
}

export async function nativeSignIn(
  deps: { signIn?: BossChatAuthPlugin['signIn']; fetch?: typeof fetch; platform?: NativePlatform | null } = {},
): Promise<NativeSignInResult> {
  const platform = deps.platform === undefined ? nativePlatform() : deps.platform
  if (!platform) return 'failed'
  const { verifier, challenge } = await pkce()
  let returned: string
  try {
    const signIn = deps.signIn ?? (await nativeSignInFn())
    returned = (await signIn({ url: appLoginUrl(platform, challenge), callbackScheme: CALLBACK_SCHEME })).url
  } catch {
    // The person closed the browser, or it could not open.
    return 'cancelled'
  }
  const code = codeFrom(returned)
  if (!code) return 'failed'
  const response = await (deps.fetch ?? fetch)('/auth/app-handoff', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'boss-chat' },
    body: JSON.stringify({ code, verifier }),
  })
  return response.ok ? 'signed-in' : 'failed'
}
